import { createSlice } from "@reduxjs/toolkit";
import axios from "../../shared/api/axios";
import Printer from "../../shared/utils/Printer";
import { Timer } from "../../shared/utils/timer";
// ----------------------------------------------------------------------

const initialState = {
  players: [],
  errorMessage: null,
  gameInfo: {
    gameID: "",
    viewedNumbers: [],
    receipts: [],
  },
  isGameProccess: false,
  isGameOver: false,
  countdown: 0,
};

const BALL_DRAW_INTERVAL_MS = 2500;

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.errorMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.message ||
    fallback
  );
}

const slice = createSlice({
  name: "keno",
  initialState,
  reducers: {
    hasError(state, action) {
      state.errorMessage = action.payload;
    },
    clearError(state) {
      state.errorMessage = null;
    },
    clearGame(state, action) {
      return initialState;
    },
    initGame(state, action) {
      state.isGameProccess = false;
      state.isGameOver = false;
      state.gameInfo = {
        ...action.payload.response,
        viewedNumbers: [],
      };
      state.timer = action.payload.timer;
    },
    showResult(state, action) {
      state.isGameOver = true;
    },
    _hideResult(state, action) {
      state.isGameOver = false;
    },
    addGameInfo(state, action) {
      state.players = action.payload.response?.players;
      state.gameInfo = {
        ...action.payload.response.gameInfo,
        viewedNumbers: [],
        hideNumbers: action.payload.response.gameInfo.winNumbers,
      };
      state.isGameOver = false;
      state.timer = action.payload.timer;
    },
    addPlayer(state, action) {
      const newPlayers = [...state?.players];
      newPlayers?.push(action.payload);
      state.players = newPlayers;
    },
    removePlayer(state, action) {
      const newDeletePlayers = [...state?.players];
      const index = newDeletePlayers?.findIndex(
        (pl) => pl.id === action.payload
      );
      if (index !== -1) newDeletePlayers?.splice(index, 1);

      state.players = newDeletePlayers;
    },
    removeAllPlayers(state) {
      state.players = [];
    },
    _viewBall(state, action) {
      state.gameInfo = action.payload;
    },
    startGame(state, action) {
      state.isGameProccess = true;
    },
    updateCountdown(state, action) {
      state.countdown = action.payload; // Met à jour le temps restant du compte à rebours
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  initGame,
  clearGame,
  startGame,
  _hideResult,
  addPlayer,
  removePlayer,
  removeAllPlayers,
  updateCountdown,
  clearError,
} = slice.actions;

// ----------------------------------------------------------------------

export function postInitGame() {
  return async (dispatch) => {
    const timer = new Timer(BALL_DRAW_INTERVAL_MS);
    try {
      const response = await axios.get("/api/v1/keno/rounds/?action=get");
      dispatch(slice.actions.initGame({ response: response.data, timer }));
      const receipts = response.data.receipts;
      if (receipts.length > 0) {
        receipts?.forEach(async (key) => {
          dispatch(slice.actions.addPlayer(key));
        });
      }
    } catch (error) {
      dispatch(slice.actions.hasError(getErrorMessage(error, "Erreur de chargement.")));
    }
  };
}

export function postAddPlayer(amount) {
  return async (dispatch, getState) => {
    const state = getState();
    const balls = state.chips.chipsNumber;
    const { gameID } = state.keno.gameInfo;
    const playersCount = state.keno.players?.length;
    try {
      const response = await axios.post("/api/v1/keno/receipts/?action=add", {
        round_id: gameID,
        numbers: [balls],
        money: String(amount),
      });
      // const printData = createPrintData(gameID, response.data, playersCount);
      const urlParams = new URLSearchParams(window.location.search);
      const isPrintingFromUrl = urlParams.get("is_printing", "0");
      if (isPrintingFromUrl === "1") {
        const printer = new Printer();
        printer.print(gameID, response.data, playersCount);
      }
      dispatch(slice.actions.addPlayer(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(getErrorMessage(error, "Erreur lors de l'ajout.")));
    }
  };
}

export function postRemovePlayer(id) {
  return async (dispatch) => {
    try {
      await axios.post(`/api/v1/keno/receipts/?action=delete`, { id });
      dispatch(slice.actions.removePlayer(id));
    } catch (error) {
      dispatch(slice.actions.hasError(getErrorMessage(error, "Erreur lors de la suppression.")));
    }
  };
}

export function postRemoveAllPlayers() {
  return async (dispatch, getState) => {
    const ids = getState().keno.players?.map((p) => p.id) || [];
    for (const id of ids) {
      await dispatch(postRemovePlayer(id));
    }
  };
}

function _viewNextBall(dispatch, getState) {
  const { gameInfo, timer } = getState().keno;
  const { hideNumbers, viewedNumbers = [] } = gameInfo;
  if (hideNumbers?.length) {
    const [currentNumber, ...newHideNumbers] = hideNumbers;
    const updatedViewedNumbers = [...viewedNumbers, currentNumber];
    const updatedGameInfo = {
      ...gameInfo,
      viewedNumbers: updatedViewedNumbers,
      hideNumbers: newHideNumbers,
    };
    dispatch(slice.actions._viewBall(updatedGameInfo));
  } else {
    timer.stop();
  }
}

function _showResult(dispatch) {
  dispatch(slice.actions.showResult());
}

export function postStartGame() {
  return async (dispatch, getState) => {
    const state = getState();
    const { timer } = state.keno;
    const { gameID } = state.keno?.gameInfo;

    try {
      timer.clearTickHandlers(); // ✅
      timer.addTickHeandler(() => _viewNextBall(dispatch, getState));

      const showResultDelay = 5 * 1000;
      timer.setOnStopHeandler(() =>
        setTimeout(() => _showResult(dispatch), showResultDelay)
      );

      let response = await axios.post("/api/v1/keno/receipts/?action=finish", {
        round_id: gameID,
      });
      if (response.data?.result) {
        dispatch(slice.actions.startGame());
        response = await axios.post("/api/v1/keno/receipts/?action=calculate", {
          round_id: gameID,
        });
        if (response.data?.result) {
          timer.start();
          dispatch(slice.actions.addGameInfo({ response: response.data, timer }));
        } else {
          dispatch(
            slice.actions.hasError(
              response.data?.errorMessage || "Erreur lors du calcul."
            )
          );
        }
      } else {
        dispatch(
          slice.actions.hasError(
            response.data?.errorMessage || "Erreur lors du demarrage."
          )
        );
      }
    } catch (error) {
      dispatch(slice.actions.hasError(getErrorMessage(error, "Erreur lors du tirage.")));
    }
  };
}
